import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import ImageUploadModal from 'components/common/ImageUploadModal';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { IMAGE_SIZES } from 'utils/constants';
import { barInfoListActions } from '../barInfoSlice';
import { GENERATED_BAR, REGULAR_BAR } from '../constants';
import { BarInfo } from '../models/IBarGuideInfo';
import { ModalProps } from '../models/IBarGuideInfoModalProps';
import './BarGuideList.scss';

const BarGuideDetail: React.FC<ModalProps> = ({ action, barInfo, onCloseModal }) => {
  const { t } = useTranslation();
  //const history = useHistory();
  const dispatch = useAppDispatch();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [showImgUploadModal, setShowImgUploadModal] = useState(false);
  const barInfoRef = useRef<BarInfo | undefined>(barInfo);

  //ref
  const divRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {
    img_en: useRef<HTMLDivElement>(null),
    img_ja: useRef<HTMLDivElement>(null),
    img_ko: useRef<HTMLDivElement>(null),
    img_vi: useRef<HTMLDivElement>(null),
    img_zh_hans: useRef<HTMLDivElement>(null),
    img_zh_hant: useRef<HTMLDivElement>(null),
  };

  const currImgAttr = useRef('');
  const currImgPath = useRef('');

  const handleSave = () => {
    if (barInfoRef.current) {
      if (action === 'Add') {
        dispatch(barInfoListActions.addNewBarInfo(barInfoRef.current));
      } else if (action === 'Update') {
        dispatch(barInfoListActions.updateBarInfo(barInfoRef.current));
      }
    }
    onCloseModal();
  };

  const handleOnChangeInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const property_name = event.currentTarget.name;
    const value = event.currentTarget.value;
    if (action === 'Add' && barInfoRef.current === undefined) {
      const bar: BarInfo = {
        id: 0,
        code: 1,
        name: '',
        title: '',
        title_en: '',
        title_ja: '',
        title_vi: '',
        title_ko: '',
        title_zh_hant: '',
        title_zh_hans: '',
        text: '',
        text_en: '',
        text_ja: '',
        text_vi: '',
        text_ko: '',
        text_zh_hant: '',
        text_zh_hans: '',
        img: '',
        img_en: '',
        img_ja: '',
        img_vi: '',
        img_ko: '',
        img_zh_hant: '',
        img_zh_hans: '',
        type: 0,
        remarks: '',
        delete_flg: false,
      };
      barInfoRef.current = bar;
    }
    updateBarInfoValue(property_name, value);
  };

  const handleResFromUploadImgModal = (filename: string, imagePath: string) => {
    const currentRef = divRefs[currImgAttr.current];
    if (currentRef.current) {
      if (filename.length === 0 || imagePath.length === 0) {
        currentRef.current.innerHTML = '';
        return;
      }

      const img = document.createElement('img');
      img.src = imagePath;
      img.id = currImgAttr.current;
      img.style.maxWidth = '240px';
      img.style.maxHeight = '165px';
      // img.alt = t('BarListTranslationFeature.Text.no_image');
      currentRef.current.innerHTML = '';
      currentRef.current.appendChild(img);
    }
    updateBarInfoValue(currImgAttr.current, filename);
  };
  //todo
  const handleChangeImage = (imgAttr: string) => {
    currImgAttr.current = imgAttr;
    const element = document.getElementById(currImgAttr.current) as HTMLImageElement | null;
    if (element instanceof HTMLImageElement) {
      currImgPath.current = element.src;
    } else {
      currImgPath.current = '';
    }
    setShowImgUploadModal(true);
  };

  const updateBarInfoValue = (property: string, value: string) => {
    if (barInfoRef.current) {
      const obj = {
        [property]: value,
      };
      barInfoRef.current = { ...barInfoRef.current, ...obj };
    }
  };

  // Memoizing the MemoizedComponent using useCallback
  const imageComponent = useCallback((id: string, file_name: string) => {
    if (file_name.length === 0) return null;

    return (
      <img
        style={{
          maxWidth: 240,
          maxHeight: 165,
        }}
        src={process.env.REACT_APP_IMAGE_URL + file_name}
        id={id}
        alt={t('BarListTranslationFeature.Text.no_image')}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEscapeKey(onCloseModal);

  return (
    <>
      {showImgUploadModal && (
        <ImageUploadModal
          imagepath={currImgPath.current}
          minWidth={IMAGE_SIZES.bar_img.minWidth}
          minHeight={IMAGE_SIZES.bar_img.minHeight}
          maxWidth={IMAGE_SIZES.bar_img.maxWidth}
          maxHeight={IMAGE_SIZES.bar_img.maxHeight}
          maxFileSize={IMAGE_SIZES.bar_img.maxFileSize}
          onReflect={handleResFromUploadImgModal}
          onClose={() => {
            setShowImgUploadModal(false);
          }}
        ></ImageUploadModal>
      )}
      <div className="static-modal">
        <Modal
          dialogClassName="form-barguide-detail"
          show
          centered
          backdrop={'static'}
          keyboard={true}
          onHide={onCloseModal}
        >
          <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <FeatureTitleBar title={t('BarListTranslationFeature.Modal.bar_information_detail')}></FeatureTitleBar>
          </Modal.Header>
          <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <div className="mb-3 d-flex justify-content-between">
              <small>
                <div dangerouslySetInnerHTML={{ __html: t('BarListTranslationFeature.Modal.notice_text') }}></div>
              </small>
              <div className="text-end">
                <button onClick={handleSave} type="button" className="btn btn--dark-green">
                  {t('BarListTranslationFeature.Button.save')}
                </button>
              </div>
            </div>
            <Row>
              <Col>
                <Table bordered>
                  <tbody>
                    <tr>
                      <th style={{ width: '45%' }} colSpan={2}>
                        {t('BarListTranslationFeature.Modal.information_name')}
                      </th>
                      <td>
                        {barInfo !== undefined ? (
                          barInfo.type === REGULAR_BAR && action === 'Update' ? (
                            <Form.Control
                              name="name"
                              onChange={handleOnChangeInput}
                              defaultValue={barInfo.name}
                              required={true}
                            ></Form.Control>
                          ) : barInfo.type === GENERATED_BAR && action === 'Update' ? (
                            <div>
                              {barInfo.name
                                .match(/\((.*?)\)/g)
                                ?.map((match) => match.slice(1, -1))
                                .join('／') || ''}
                            </div>
                          ) : (
                            <div></div>
                          )
                        ) : action === 'Add' ? (
                          <Form.Control onChange={handleOnChangeInput} required={true} name="name"></Form.Control>
                        ) : (
                          <div></div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th rowSpan={7}>{t('BarListTranslationFeature.Modal.title')}</th>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.japanese')}</th>
                      <td>
                        <Form.Control
                          name="title_ja"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_ja}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.english')}</th>
                      <td>
                        <Form.Control
                          name="title_en"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_en}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.simplified_chinese')}</th>
                      <td>
                        <Form.Control
                          name="title_zh_hans"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_zh_hans}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.traditional_chinese')}</th>
                      <td>
                        <Form.Control
                          name="title_zh_hant"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_zh_hant}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.korean')}</th>
                      <td>
                        <Form.Control
                          name="title_ko"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_ko}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.vietnamese')}</th>
                      <td>
                        <Form.Control
                          name="title_vi"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.title_vi}
                        ></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th rowSpan={7}>{t('BarListTranslationFeature.Modal.information_text')}</th>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.japanese')}</th>
                      <td>
                        <textarea
                          className="form-control"
                          rows={2}
                          name="text_ja"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_ja}
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.english')}</th>
                      <td>
                        <textarea
                          className="form-control"
                          rows={2}
                          name="text_en"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_en}
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.simplified_chinese')}</th>
                      <td>
                        <textarea
                          rows={2}
                          name="text_zh_hans"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_zh_hans}
                          className="form-control"
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.traditional_chinese')}</th>
                      <td>
                        <textarea
                          rows={2}
                          name="text_zh_hant"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_zh_hant}
                          className="form-control"
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.korean')}</th>
                      <td>
                        <textarea
                          rows={2}
                          name="text_ko"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_ko}
                          className="form-control"
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.vietnamese')}</th>
                      <td>
                        <textarea
                          rows={2}
                          name="text_vi"
                          onChange={handleOnChangeInput}
                          defaultValue={barInfo?.text_vi}
                          className="form-control"
                        ></textarea>
                      </td>
                    </tr>
                    <tr>
                      <th rowSpan={7}>{t('BarListTranslationFeature.Modal.image')}</th>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.japanese')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_ja} className="d-flex align-items-center">
                            {barInfo && barInfo.img_ja && imageComponent('img_ja', barInfo.img_ja)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_ja')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.english')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_en}>
                            {barInfo && barInfo.img_en && imageComponent('img_en', barInfo.img_en)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_en')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.simplified_chinese')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_zh_hans}>
                            {barInfo && barInfo.img_zh_hans && imageComponent('img_zh_hans', barInfo.img_zh_hans)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_zh_hans')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.traditional_chinese')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_zh_hant}>
                            {barInfo && barInfo.img_zh_hant && imageComponent('img_zh_hant', barInfo.img_zh_hant)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_zh_hant')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.korean')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_ko}>
                            {barInfo && barInfo.img_ko && imageComponent('img_ko', barInfo.img_ko)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_ko')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('BarListTranslationFeature.Modal.vietnamese')}</th>
                      <td>
                        <div className="text-center gap-3 d-flex justify-content-between">
                          <div ref={divRefs.img_vi}>
                            {barInfo && barInfo.img_vi && imageComponent('img_vi', barInfo.img_vi)}
                          </div>
                          <div className="align-self-end">
                            <Button onClick={handleChangeImage.bind(null, 'img_vi')}>
                              {t('BarListTranslationFeature.Button.change')}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th colSpan={2}>{t('BarListTranslationFeature.Modal.remarks')}</th>
                      <td>
                        <textarea
                          rows={4}
                          onChange={handleOnChangeInput}
                          name="remarks"
                          className="form-control"
                          defaultValue={barInfo?.remarks}
                        ></textarea>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Modal.Body>
          {/* <Modal.Footer>
            <button className="btn btn-sm btn-outline-danger" onClick={onCloseModal}>
              {t('BarListTranslationFeature.Button.close')}
            </button>
          </Modal.Footer> */}
        </Modal>
      </div>
    </>
  );
};
export default BarGuideDetail;
